import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Environment } from '@aptsi-types';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { EnvironmentService as CoreEnvironmentService } from 'src/app/core/service/environment.service';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { EnvironmentService } from '../../services/environment.service';
import { WorkflowNavigationService } from '../../../shared/workflow-navigation.service';

@Component({
  selector: 'app-environment-create-or-update',
  templateUrl: './environment-create-or-update.component.html',
  styleUrls: ['./environment-create-or-update.component.scss']
})
export class EnvironmentCreateOrUpdateComponent implements OnInit {
  isCreate = true;
  existingEnvironmentId: string | null = null;
  environment: Environment | null = null;
  organizationId = '';
  form!: FormGroup;

  loading = false;
  saving = false;
  pageError = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly environmentService: EnvironmentService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly keycloakStateService: KeycloakStateService,
    public readonly coreEnvironmentService: CoreEnvironmentService,
     private readonly workflowNavigation: WorkflowNavigationService,
  ) {}

  ngOnInit(): void {
    this.organizationId = this.keycloakStateService.getOrganizationId() ?? '';
    this.isCreate = Boolean(this.route.snapshot.data['create']);
    this.existingEnvironmentId =
      this.route.snapshot.paramMap.get('environmentId') ??
      this.route.snapshot.params['environmentId'] ??
      null;

    // Build the form before requesting existing data so fetchEnvironment can safely patch it.
    this.form = new FormGroup({
      environmentId: new FormControl(this.isCreate ? null : this.existingEnvironmentId),
      environmentName: new FormControl('', [Validators.required, Validators.maxLength(120)]),
      description: new FormControl('', [Validators.required, Validators.maxLength(1000)])
    });

    if (!this.isCreate) {
      if (!this.existingEnvironmentId) {
        this.pageError = 'Environment ID is missing. Unable to load this environment.';
        return;
      }
      void this.fetchEnvironment();
    }
  }

  get pageTitle(): string {
    return this.isCreate ? 'Add Environment' : 'Update Environment';
  }

  get pageDescription(): string {
    return this.isCreate
      ? 'Create an environment to organize and describe a deployment context.'
      : 'Review the environment details and save any required changes.';
  }

  get controls(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  async fetchEnvironment(): Promise<void> {
    if (!this.existingEnvironmentId) {
      this.pageError = 'Environment ID is missing. Unable to load this environment.';
      return;
    }

    this.loading = true;
    this.pageError = '';

    try {
      const response: any = await this.environmentService
        .getEnvironment(this.existingEnvironmentId)
        .toPromise();

      this.environment = response?.['Resource retrieved successfully']?.[0] ?? null;
      if (!this.environment) {
        this.pageError = 'No details were found for this environment.';
        return;
      }

      this.form.patchValue({
        environmentId: this.environment.id ?? this.existingEnvironmentId,
        environmentName: this.environment.environmentName ?? '',
        description: this.environment.description ?? ''
      });
    } catch (error: any) {
      console.error('Error fetching environment:', error);
      this.pageError = this.extractMessage(error) || 'Unable to load the environment details. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  isInvalid(controlName: string): boolean {
    const control = this.form?.get(controlName);
    return Boolean(control && control.invalid && (control.touched || control.dirty));
  }

  async submit(): Promise<void> {
    if (this.saving || this.loading) {
      return;
    }

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.pageError = 'Review the highlighted fields and correct the validation errors.';
      return;
    }

    this.pageError = '';
    this.saving = true;
    const value = this.form.getRawValue();

    try {
      if (this.isCreate) {
        await this.environmentService
          .createEnvironment(
            String(value.environmentName).trim(),
            String(value.description).trim(),
            this.organizationId
          )
          .toPromise();
      } else {
        const environmentId = String(value.environmentId ?? this.existingEnvironmentId ?? '').trim();
        if (!environmentId) {
          this.pageError = 'Environment ID is missing. Unable to update this environment.';
          return;
        }

        await this.environmentService
          .updateEnvironment(
            environmentId,
            String(value.environmentName).trim(),
            String(value.description).trim(),
            this.organizationId
          )
          .toPromise();
      }

      this.openSuccessDialog(this.isCreate);
    } catch (error: any) {
      const message = this.extractMessage(error) || 'An unexpected error occurred while saving the environment.';
      this.pageError = message;
      this.openErrorDialog(message);
    } finally {
      this.saving = false;
    }
  }

    cancel(): void {
      this.navigateToEnvironment();
    }

openSuccessDialog(isCreate: boolean): void {
  this.dialog.open(SuccessDialogComponent, {
    width: '380px',
    data: {
      isCreate,
      isCreateUpdate: true,
      title: 'Environment',
      message: isCreate
        ? 'Environment created successfully.'
        : 'Environment updated successfully.',
    },
  }).afterClosed().subscribe(() => {
    this.navigateToEnvironment();
  });
}



  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '380px',
      data: { errorMessage }
    });
  }

  /**
 * Return to the Environment search page within the current product.
 */
    private navigateToEnvironment(): void {
      void this.workflowNavigation.navigateToWorkflow(
        'securityZoneManagement',
        ['environment', 'search']
      );
    }

  private extractMessage(responseOrError: any): string {
    if (!responseOrError) {
      return '';
    }

    if (typeof responseOrError === 'string') {
      return responseOrError;
    }

    const candidates = [
      responseOrError.message,
      responseOrError.error?.message,
      responseOrError.error?.errorMessage,
      responseOrError.error?.details,
      responseOrError.errorMessage
    ];

    const message = candidates.find(candidate => typeof candidate === 'string' && candidate.trim());
    return message ? String(message).trim() : '';
  }
}
